import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { normalizePeriodName } from './scheduleTargetUtils';
import { kgToLb, checkBPValues, checkPulseValue, checkBGValue, checkWeightValue } from './measurementUtils';

const DEFAULT_BP_TARGET = {
  systolicMin: 100,
  systolicMax: 140,
  diastolicMin: 60,
  diastolicMax: 90,
  pulseMin: 60,
  pulseMax: 100
};
const DEFAULT_BG_TARGET = { minimum: 60, maximum: 110 };
const DEFAULT_WEIGHT_TARGET = { weightMin: 66, weightMax: 220 };

const TABLE_STYLES = {
  headStyles: {
    fillColor: [0, 119, 182],
    textColor: [255, 255, 255],
    fontStyle: 'bold',
    fontSize: 7.5,
    halign: 'center',
    valign: 'middle'
  },
  bodyStyles: {
    fontSize: 7,
    halign: 'center',
    valign: 'middle',
    textColor: [20, 20, 20]
  },
  alternateRowStyles: { fillColor: [248, 250, 252] },
  styles: {
    cellPadding: 1.5,
    overflow: 'linebreak',
    lineWidth: 0.1,
    lineColor: [210, 220, 230]
  },
  margin: { top: 12, bottom: 12, left: 8, right: 8 }
};

function sortMeasurementsByTime(measurements = []) {
  return [...measurements].sort((a, b) => {
    const timeA =
      a.measurement_time ||
      a.measure_new_date_time?.split('T')[1] ||
      a.measure_date_time?.split(' ')[1] ||
      a.created_at?.split('T')[1] ||
      '00:00:00';
    const timeB =
      b.measurement_time ||
      b.measure_new_date_time?.split('T')[1] ||
      b.measure_date_time?.split(' ')[1] ||
      b.created_at?.split('T')[1] ||
      '00:00:00';
    return timeB.localeCompare(timeA);
  });
}

function joinCell(values) {
  return values
    .map((v) => (v == null || v === '' ? null : String(v)))
    .filter((v) => v != null)
    .join('\n');
}

function roundValue(value) {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (Number.isNaN(n)) return '';
  return String(Math.round(n));
}

function fixed1(value) {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (Number.isNaN(n)) return '';
  return n.toFixed(1);
}

function formatAvg(values) {
  if (!values.length) return '';
  const avg = values.reduce((a, b) => a + b, 0) / values.length;
  return Number.isInteger(avg) ? String(avg) : avg.toFixed(2);
}

function getMeasureTimeRaw(measurement) {
  return (
    measurement.measurement_time ||
    measurement.measure_new_date_time?.split('T')[1]?.slice(0, 5) ||
    measurement.measure_date_time?.split(' ')[1]?.slice(0, 5) ||
    ''
  );
}

function savePdf(doc, filename) {
  doc.save(filename);
}

function addReportHeader(doc, startY, { title, patient }) {
  const cardX = 10;
  const cardY = startY;
  const cardW = 260;
  const cardH = 26;

  // Background and border for header card (matching print UI card)
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(203, 213, 225);
  doc.setLineWidth(0.3);
  doc.roundedRect(cardX, cardY, cardW, cardH, 2, 2, 'FD');

  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(0, 119, 182);
  doc.text(title || 'Blood Pressure Report', cardX + 4, cardY + 6);

  // Timestamp on right
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  const timeText = `Printed On: ${new Date().toLocaleString()}`;
  const timeWidth = doc.getTextWidth(timeText);
  doc.text(timeText, cardX + cardW - 4 - timeWidth, cardY + 6);

  // Blue accent line under title
  doc.setDrawColor(0, 119, 182);
  doc.setLineWidth(0.4);
  doc.line(cardX + 4, cardY + 8, cardX + cardW - 4, cardY + 8);

  // Info items in 3 columns
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(30, 41, 59);

  const col1X = cardX + 4;
  const col2X = cardX + 90;
  const col3X = cardX + 175;

  const yRow1 = cardY + 14;
  const yRow2 = cardY + 21;

  const printVal = (label, val, x, y) => {
    doc.setFont('helvetica', 'bold');
    doc.text(`${label}: `, x, y);
    const labelW = doc.getTextWidth(`${label}: `);
    doc.setFont('helvetica', 'normal');
    doc.text(String(val || 'N/A'), x + labelW, y);
  };

  printVal('Patient Name', patient?.name, col1X, yRow1);
  printVal('DOB', patient?.dob, col2X, yRow1);
  printVal('Phone', patient?.phone, col3X, yRow1);

  printVal('Device', patient?.device, col1X, yRow2);
  printVal('Practice', patient?.practice, col2X, yRow2);
  printVal('Printed By', patient?.printedBy || patient?.printed_by || 'System User', col3X, yRow2);

  return cardY + cardH + 6;
}

function getColorForStatus(status) {
  if (status === 'high') return [211, 47, 47];
  if (status === 'low') return [245, 124, 0];
  return [21, 128, 61];
}

function renderTables(doc, startY, tables) {
  let y = startY;

  tables.forEach((table, index) => {
    if (!table?.body?.length && !table?.head?.length) return;

    if (table.title) {
      if (y > 170) {
        doc.addPage();
        y = 12;
      }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(15, 23, 42);
      doc.text(table.title, 10, y);
      y += 5;
    }

    if (table.subtitle) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(100, 116, 139);
      doc.text(table.subtitle, 10, y);
      y += 4;
    }

    autoTable(doc, {
      startY: y,
      head: table.head,
      body: table.body,
      columnStyles: table.columnStyles || {},
      ...TABLE_STYLES,
      theme: 'grid',
      showHead: table.head?.length ? 'everyPage' : false,
      rowPageBreak: 'avoid',
      willDrawCell: (data) => {
        const raw = data.cell.raw;
        if (raw && typeof raw === 'object' && raw.isMeasurementCell) {
          const isAlt = data.row.index % 2 === 1;
          // Hide standard text so it doesn't overlap under didDrawCell custom text
          data.cell.styles.textColor = isAlt ? [248, 250, 252] : [255, 255, 255];
        }
      },
      didDrawCell: (data) => {
        const raw = data.cell.raw;
        if (raw && typeof raw === 'object' && raw.isMeasurementCell && raw.coloredLines) {
          const cell = data.cell;
          const lines = raw.coloredLines;
          const isAlt = data.row.index % 2 === 1;
          const bgColor = isAlt ? [248, 250, 252] : [255, 255, 255];
          doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
          doc.rect(cell.x + 0.1, cell.y + 0.1, cell.width - 0.2, cell.height - 0.2, 'F');
          doc.setDrawColor(226, 232, 240);
          doc.setLineWidth(0.1);
          doc.rect(cell.x, cell.y, cell.width, cell.height, 'S');
          const centerX = cell.x + cell.width / 2;
          let currentY = cell.y + 3.5;
          const maxY = cell.y + cell.height - 1.2;
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(raw.pdfFontSize || 8);
          lines.forEach((line) => {
            if (currentY > maxY) return;
            const text = String(line.text ?? '');
            const color = line.color || [21, 128, 61];
            doc.setTextColor(color[0], color[1], color[2]);
            const textW = doc.getTextWidth(text);
            doc.text(text, centerX - textW / 2, currentY);
            currentY += 3.2;
          });
          return;
        }
        if (raw && typeof raw === 'object' && raw.isMeasurementCell && raw.readingBlocks) {
          const cell = data.cell;
          const blocks = raw.readingBlocks;
          if (!blocks || !blocks.length) return;

          const isAlt = data.row.index % 2 === 1;
          const bgColor = isAlt ? [248, 250, 252] : [255, 255, 255];

          doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
          doc.rect(cell.x + 0.1, cell.y + 0.1, cell.width - 0.2, cell.height - 0.2, 'F');
          doc.setDrawColor(226, 232, 240);
          doc.setLineWidth(0.1);
          doc.rect(cell.x, cell.y, cell.width, cell.height, 'S');

          const centerX = cell.x + cell.width / 2;
          const maxY = cell.y + cell.height - 1.5;
          let currentY = cell.y + 3.5;

          blocks.forEach((b, idx) => {
            if (currentY > maxY) return;

            if (idx > 0) {
              doc.setDrawColor(203, 213, 225);
              doc.setLineWidth(0.2);
              doc.setLineDashPattern([1, 1], 0);
              doc.line(cell.x + 3, currentY - 1.5, cell.x + cell.width - 3, currentY - 1.5);
              doc.setLineDashPattern([], 0);
              currentY += 1.5;
            }

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(raw.pdfFontSize || 8);

            const sysText = String(b.sys || '—');
            const diaText = String(b.dia || '—');
            const slashText = '/';

            const sysW = doc.getTextWidth(sysText);
            const slashW = doc.getTextWidth(slashText);
            const diaW = doc.getTextWidth(diaText);
            const totalBpW = sysW + slashW + diaW;
            let startX = centerX - totalBpW / 2;

            if (currentY <= maxY) {
              doc.setTextColor(b.sysColor[0], b.sysColor[1], b.sysColor[2]);
              doc.text(sysText, startX, currentY);
              startX += sysW;

              doc.setTextColor(148, 163, 184);
              doc.text(slashText, startX, currentY);
              startX += slashW;

              doc.setTextColor(b.diaColor[0], b.diaColor[1], b.diaColor[2]);
              doc.text(diaText, startX, currentY);
            }

            currentY += 3.2;

            if (b.pulse && currentY <= maxY) {
              doc.setFont('helvetica', 'normal');
              doc.setFontSize(7.0);
              doc.setTextColor(b.pulseColor[0], b.pulseColor[1], b.pulseColor[2]);
              const pulseW = doc.getTextWidth(b.pulse);
              doc.text(b.pulse, centerX - pulseW / 2, currentY);
              currentY += 2.8;
            }

            if (b.time && currentY <= maxY) {
              doc.setFont('helvetica', 'normal');
              doc.setFontSize(6.5);
              doc.setTextColor(100, 116, 139);
              const timeW = doc.getTextWidth(b.time);
              doc.text(b.time, centerX - timeW / 2, currentY);
              currentY += 2.8;
            }

            currentY += 1.0;
          });
        }
      }
    });

    y = (doc.lastAutoTable?.finalY || y) + (index < tables.length - 1 ? 8 : 0);
  });

  return y;
}

function addPageNumbers(doc) {
  const pageCount = doc.internal.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
    doc.setPage(pageNumber);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(100, 116, 139);
    doc.text(`Page ${pageNumber} of ${pageCount}`, pageWidth - 10, pageHeight - 5, { align: 'right' });
  }
}

export function exportMeasurementTablesPdf({ filename, title, patient, tables }) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
  const startY = addReportHeader(doc, 12, { title, patient });
  renderTables(doc, startY, tables);
  addPageNumbers(doc);
  savePdf(doc, filename);
}

function buildBpMeasurementTable({
  periodList,
  sortedDates,
  groupedMeasurements,
  getResolvedPeriodName,
  getPeriodInfo,
  getTargetForPeriod,
  checkBPValues: checkBPValuesOverride,
  getReadingStatus,
  formatDate,
  formatTime
}) {
  const headRow1 = [{ content: 'DATE', rowSpan: 2, styles: { halign: 'center' } }];
  const headRow2 = [];

  periodList.forEach((periodName) => {
    const periodInfo = getPeriodInfo ? getPeriodInfo(periodName) : null;
    const timeRange =
      periodInfo?.startTimeDisplay && periodInfo?.endTimeDisplay
        ? `${periodInfo.startTimeDisplay}–${periodInfo.endTimeDisplay}`
        : '';
    const headerText = timeRange ? `${periodName.toUpperCase()}\n${timeRange}` : periodName.toUpperCase();
    headRow1.push({ content: headerText, colSpan: 3, styles: { halign: 'center' } });
    headRow2.push(
      { content: 'BP', styles: { fontSize: 5.5, cellPadding: 0.8 } },
      { content: 'PULSE', styles: { fontSize: 5.5, cellPadding: 0.8 } },
      { content: 'STATUS', styles: { fontSize: 5.5, cellPadding: 0.8 } }
    );
  });

  const body = [];

  sortedDates.forEach((date) => {
    const dateMeasurements = groupedMeasurements[date] || [];
    const byPeriod = {};

    dateMeasurements.forEach((m) => {
      const resolved = getResolvedPeriodName(m);
      const matched = periodList.find((p) => normalizePeriodName(p) === normalizePeriodName(resolved));
      if (!matched) return;
      if (!byPeriod[matched]) byPeriod[matched] = [];
      byPeriod[matched].push(m);
    });

    periodList.forEach((periodName) => {
      byPeriod[periodName] = sortMeasurementsByTime(byPeriod[periodName] || []);
    });

    const dateRowCount = Math.max(1, ...periodList.map((periodName) => byPeriod[periodName].length));

    for (let rowIndex = 0; rowIndex < dateRowCount; rowIndex += 1) {
      const row = [];
      if (rowIndex === 0) {
        row.push({
          content: formatDate(date),
          rowSpan: dateRowCount,
          styles: { fontStyle: 'bold', valign: 'middle' }
        });
      }

      periodList.forEach((periodName) => {
        const m = byPeriod[periodName][rowIndex];
        if (!m) {
          row.push(
            { content: rowIndex === 0 ? '—' : '', styles: { textColor: [148, 163, 184] } },
            { content: rowIndex === 0 ? '—' : '', styles: { textColor: [148, 163, 184] } },
            ''
          );
          return;
        }

        const sys = roundValue(m.systolic_pressure ?? m.systolic);
        const dia = roundValue(m.diastolic_pressure ?? m.diastolic);
        const readingStatus = typeof getReadingStatus === 'function' ? getReadingStatus(m, periodName) : null;
        const target = readingStatus
          ? null
          : ((typeof getTargetForPeriod === 'function' && getTargetForPeriod(periodName)) || DEFAULT_BP_TARGET);
        const bpChecker = typeof checkBPValuesOverride === 'function' ? checkBPValuesOverride : checkBPValues;
        const bpCheck = readingStatus || bpChecker(m, target);
        const pulseStatus = readingStatus?.pulseStatus
          || checkPulseValue(m.pulse, target || DEFAULT_BP_TARGET)
          || 'normal';

        const sysColor = getColorForStatus(bpCheck.sysStatus);
        const diaColor = getColorForStatus(bpCheck.diaStatus);

        const pulseVal = m.pulse != null && m.pulse !== '' ? roundValue(m.pulse) : null;
        const pulseColor = getColorForStatus(pulseStatus);
        const timeRaw = getMeasureTimeRaw(m);
        const timeStr = timeRaw ? formatTime(timeRaw) : '';
        const isAbnormal =
          bpCheck.sysStatus === 'high' ||
          bpCheck.sysStatus === 'low' ||
          bpCheck.diaStatus === 'high' ||
          bpCheck.diaStatus === 'low' ||
          pulseStatus === 'high' ||
          pulseStatus === 'low';
        const isReviewed = Boolean(m.reviewed_at || m.reviewed_by || m.reviewed_by_name);
        const isAssigned = Boolean(m.assigned_to_user_id || m.assigned_to_name);
        const reviewStatus = !isAbnormal
          ? ''
          : isReviewed
            ? 'R'
            : isAssigned
              ? 'A'
              : 'P';
        const reviewColor = isReviewed
          ? [21, 128, 61]
          : isAssigned
            ? [2, 132, 199]
            : [217, 119, 6];

        const bpBlock = {
          sys,
          dia,
          sysColor,
          diaColor,
          time: timeStr
        };

        const pulseCell = pulseVal
          ? coloredValueCell([{ text: pulseVal, color: pulseColor }])
          : { content: '—', styles: { textColor: [148, 163, 184] } };
        if (pulseCell && typeof pulseCell === 'object') {
          pulseCell.pdfFontSize = 6.5;
          pulseCell.styles = { ...(pulseCell.styles || {}), fontSize: 6.5, cellPadding: 0.8 };
        }

        row.push(
          {
            content: `${sys || '—'}/${dia || '—'}${timeStr ? `\n${timeStr}` : ''}`,
            isMeasurementCell: true,
            readingBlocks: [bpBlock],
            pdfFontSize: 7,
            styles: { minCellHeight: 14, fontSize: 7, cellPadding: 0.8 }
          },
          pulseCell,
          reviewStatus
            ? {
                content: reviewStatus,
                styles: {
                  textColor: reviewColor,
                  fontStyle: 'bold',
                  fontSize: 8,
                  halign: 'center',
                  cellPadding: 0.8
                }
              }
            : ''
        );
      });

      body.push(row);
    }
  });

  const numPeriods = periodList.length || 5;
  const dateColWidth = 24;
  const periodWidth = (260 - dateColWidth) / numPeriods;
  const bpColWidth = periodWidth * 0.46;
  const pulseColWidth = periodWidth * 0.22;
  const statusColWidth = periodWidth - bpColWidth - pulseColWidth;

  const colStyles = { 0: { cellWidth: dateColWidth } };
  for (let i = 0; i < numPeriods; i++) {
    const firstCol = 1 + (i * 3);
    colStyles[firstCol] = { cellWidth: bpColWidth };
    colStyles[firstCol + 1] = { cellWidth: pulseColWidth };
    colStyles[firstCol + 2] = { cellWidth: statusColWidth };
  }

  return {
    title: 'Blood Pressure Measurements',
    head: [headRow1, headRow2],
    body,
    columnStyles: colStyles
  };
}

function buildBpSummaryTable({ periodList, calculatePeriodStats, effectiveMeasurements, totals, formatPercentCount, getPeriodInfo }) {
  const headRow = [
    { content: 'Period', styles: { halign: 'left' } },
    { content: 'Systolic (avg / max / min)', styles: { halign: 'center' } },
    { content: 'Diastolic (avg / max / min)', styles: { halign: 'center' } },
    { content: 'Pulse (avg / max / min)', styles: { halign: 'center' } }
  ];

  const fmt = (v) => (v != null && !Number.isNaN(v) ? Math.round(v) : '—');

  const body = periodList.map((periodName) => {
    const periodInfo = getPeriodInfo ? getPeriodInfo(periodName) : null;
    const timeRange =
      periodInfo?.startTimeDisplay && periodInfo?.endTimeDisplay
        ? `${periodInfo.startTimeDisplay}–${periodInfo.endTimeDisplay}`
        : '';
    const stats = calculatePeriodStats(effectiveMeasurements, periodName);
    const hasData = (stats.count || 0) > 0;

    const labelCell = timeRange ? `${periodName} (${timeRange})` : periodName;

    if (!hasData) {
      return [labelCell, 'No readings', 'No readings', 'No readings'];
    }

    const sysStr = `${fmt(stats.avg?.sys)} (${fmt(stats.min?.sys)}–${fmt(stats.max?.sys)})`;
    const diaStr = `${fmt(stats.avg?.dia)} (${fmt(stats.min?.dia)}–${fmt(stats.max?.dia)})`;
    const pulseStr = `${fmt(stats.avg?.pulse)} (${fmt(stats.min?.pulse)}–${fmt(stats.max?.pulse)})`;

    return [labelCell, sysStr, diaStr, pulseStr];
  });

  if (totals && formatPercentCount) {
    body.push([
      'Target Breakdown',
      `Above Target (High): ${formatPercentCount(totals.above?.sys || 0, totals.sysTotal)} Sys`,
      `Within Target (Normal): ${formatPercentCount(totals.within?.sys || 0, totals.sysTotal)} Sys`,
      `Below Target (Low): ${formatPercentCount(totals.below?.sys || 0, totals.sysTotal)} Sys`
    ]);
  }

  return {
    title: 'Summary Of Measurement',
    head: [headRow],
    body,
    columnStyles: {
      0: { cellWidth: 55, halign: 'left' },
      1: { cellWidth: 68, halign: 'center' },
      2: { cellWidth: 68, halign: 'center' },
      3: { cellWidth: 69, halign: 'center' }
    }
  };
}

export function exportBloodPressureTablesPdf(config) {
  const periodList =
    config.periodList?.length > 0
      ? config.periodList
      : ['Wake-Up', 'Morning', 'Noon', 'Afternoon', 'Evening'];

  const tables = [
    buildBpMeasurementTable({ ...config, periodList }),
    buildBpSummaryTable({ ...config, periodList })
  ];

  exportMeasurementTablesPdf({
    filename: config.filename,
    title: config.title || 'Blood Pressure Report',
    patient: config.patient,
    tables
  });
}

function coloredValueCell(lines) {
  const safeLines = (lines || []).filter((line) => line && line.text != null && line.text !== '');
  if (!safeLines.length) return '—';
  const text = safeLines.map((line) => line.text).join('\n');
  return {
    content: text,
    isMeasurementCell: true,
    coloredLines: safeLines,
    styles: { minCellHeight: Math.max(8, safeLines.length * 4.2 + 2), halign: 'center' }
  };
}

function normalizeDateBuckets(dateBuckets = []) {
  if (!Array.isArray(dateBuckets) || dateBuckets.length === 0) return [];
  // Accept either [{ label, dates }] or a flat list of YYYY-MM-DD strings
  if (typeof dateBuckets[0] === 'string') {
    return [{ label: 'All Dates', dates: dateBuckets }];
  }
  return dateBuckets
    .map((bucket) => ({
      label: bucket?.label || 'All Dates',
      dates: Array.isArray(bucket?.dates) ? bucket.dates : []
    }))
    .filter((bucket) => bucket.dates.length > 0);
}

function buildBgMeasurementTables({
  dateBuckets,
  periodNames,
  groupedMeasurements,
  determinePeriodFromMeasurement,
  formatDate,
  formatTime,
  getGlucoseStatus
}) {
  const buckets = normalizeDateBuckets(dateBuckets);
  if (!buckets.length) {
    return [{ title: 'Blood Glucose', body: [['No measurement data available']] }];
  }

  return buckets.map((bucket) => {
    const headRow1 = [{ content: 'Measurement\nDate', rowSpan: 2 }];
    const headRow2 = [];

    periodNames.forEach((periodName) => {
      headRow1.push({ content: periodName, colSpan: 2 });
      headRow2.push('Blood Glucose', 'Measure Time');
    });

    const body = [];

    bucket.dates.forEach((date) => {
      const dateMeasurements = groupedMeasurements[date] || [];
      const byPeriod = {};

      dateMeasurements.forEach((m) => {
        const period = determinePeriodFromMeasurement(m);
        const matched = periodNames.find((p) => normalizePeriodName(p) === normalizePeriodName(period || ''));
        if (!matched) return;
        if (!byPeriod[matched]) byPeriod[matched] = [];
        byPeriod[matched].push(m);
      });

      const row = [formatDate(date)];
      periodNames.forEach((periodName) => {
        const readings = sortMeasurementsByTime(byPeriod[periodName] || []);
        row.push(
          coloredValueCell(
            readings.map((m) => {
              const value = roundValue(m.blood_glucose_value_1 || m.blood_glucose_value);
              const status = typeof getGlucoseStatus === 'function'
                ? getGlucoseStatus(m, periodName)
                : checkBGValue(m, DEFAULT_BG_TARGET);
              return { text: value, color: getColorForStatus(status) };
            })
          ),
          joinCell(readings.map((m) => (getMeasureTimeRaw(m) ? formatTime(getMeasureTimeRaw(m)) : '')))
        );
      });
      body.push(row);
    });

    const avgRow = ['Average'];
    periodNames.forEach((periodName) => {
      const readings = [];
      bucket.dates.forEach((date) => {
        const dateMeasurements = groupedMeasurements[date] || [];
        dateMeasurements.forEach((m) => {
          const period = determinePeriodFromMeasurement(m);
          if (normalizePeriodName(period) === normalizePeriodName(periodName)) readings.push(m);
        });
      });
      const values = readings
        .map((m) => parseFloat(m.blood_glucose_value_1 || m.blood_glucose_value))
        .filter((v) => !Number.isNaN(v));
      avgRow.push(values.length ? roundValue(formatAvg(values)) : '-', '');
    });
    body.push(avgRow);

    return {
      title: 'Blood Glucose',
      subtitle: bucket.label,
      head: [headRow1, headRow2],
      body
    };
  });
}

function buildBgSummaryTable({
  summaryPeriodColumns,
  calculatePeriodStats,
  effectiveMeasurements,
  rangeTotals,
  totalReadingCount,
  formatPercentCount,
  totalRowLayout
}) {
  const headRow1 = [{ content: 'Statistics', rowSpan: 2 }];
  const headRow2 = [];
  const pct = typeof formatPercentCount === 'function'
    ? formatPercentCount
    : (n, t) => (t ? `${Math.round((Number(n || 0) / t) * 100)}% (${n || 0})` : `0% (${n || 0})`);
  const layout = {
    beforeMealCols: Math.max(Number(totalRowLayout?.beforeMealCols) || 1, 1),
    afterMealCols: Math.max(Number(totalRowLayout?.afterMealCols) || 1, 1),
    remainingCols: Math.max(Number(totalRowLayout?.remainingCols) || 1, 1)
  };
  const columns = (summaryPeriodColumns || []).map((col) => (
    typeof col === 'string'
      ? { periodName: col, group: col, sub: col, isSingleHeader: true }
      : col
  ));

  const groups = [];
  columns.forEach((col) => {
    const existing = groups.find((g) => g.group === col.group);
    if (existing) existing.items.push(col);
    else groups.push({ group: col.group, items: [col] });
  });

  groups.forEach((group) => {
    if (group.items.length === 1 && group.items[0].isSingleHeader) {
      headRow1.push({ content: group.group, rowSpan: 2 });
    } else {
      headRow1.push({ content: group.group, colSpan: group.items.length });
      group.items.forEach((item) => {
        if (!item.isSingleHeader) headRow2.push(item.sub || item.periodName);
      });
    }
  });

  const numericStat = (value) => {
    if (value == null) return null;
    if (typeof value === 'object') {
      return value.glucose ?? value.bg ?? value.value ?? null;
    }
    return value;
  };

  const statTypes = ['Average', 'Maximum', 'Minimum', 'Count', 'Above Target', 'Below Target', 'Within Target'];
  const body = statTypes.map((statType) => {
    const row = [statType];
    columns.forEach((col) => {
      const stats = typeof calculatePeriodStats === 'function'
        ? calculatePeriodStats(effectiveMeasurements, col.periodName)
        : {};
      if (statType === 'Average') row.push(numericStat(stats.avg) != null ? roundValue(numericStat(stats.avg)) : '');
      else if (statType === 'Maximum') row.push(numericStat(stats.max) != null ? roundValue(numericStat(stats.max)) : '');
      else if (statType === 'Minimum') row.push(numericStat(stats.min) != null ? roundValue(numericStat(stats.min)) : '');
      else if (statType === 'Count') row.push(String(stats.count ?? ''));
      else if (statType === 'Above Target') row.push(String(stats.above ?? ''));
      else if (statType === 'Below Target') row.push(String(stats.below ?? ''));
      else if (statType === 'Within Target') row.push(String(stats.within ?? ''));
    });
    return row;
  });

  body.push([
    'Total',
    {
      content: `Below Target: ${pct(rangeTotals?.below, totalReadingCount)}`,
      colSpan: layout.beforeMealCols
    },
    {
      content: `Above Target: ${pct(rangeTotals?.above, totalReadingCount)}`,
      colSpan: layout.afterMealCols
    },
    {
      content: `Within Target: ${pct(rangeTotals?.within, totalReadingCount)}`,
      colSpan: layout.remainingCols
    }
  ]);

  return { title: 'Summary Of Measurement', head: headRow2.length ? [headRow1, headRow2] : [headRow1], body };
}

export function exportBloodGlucoseTablesPdf(config) {
  const tables = [
    ...buildBgMeasurementTables(config),
    buildBgSummaryTable(config)
  ];

  exportMeasurementTablesPdf({
    filename: config.filename,
    title: config.title || 'Blood Glucose Report',
    patient: config.patient,
    tables
  });
}

function buildWeightMeasurementTables({
  dateBuckets,
  periodList,
  groupedMeasurements,
  resolvePeriodForMeasurement,
  formatDate,
  formatTime,
  getWeightStatus
}) {
  const buckets = normalizeDateBuckets(dateBuckets);
  if (!buckets.length) {
    return [{ title: 'Weight', body: [['No measurement data available']] }];
  }

  return buckets.map((bucket) => {
    const headRow1 = [{ content: 'Date', rowSpan: 2 }];
    const headRow2 = [];

    periodList.forEach((periodName) => {
      headRow1.push({ content: periodName, colSpan: 4 });
      headRow2.push('Weight', 'Body Fat', 'BMI', 'Measure Time');
    });

    const body = [];

    const resolveByPeriod = (date) => {
      if (typeof resolvePeriodForMeasurement !== 'function') {
        return Object.fromEntries(periodList.map((p) => [p, []]));
      }
      const resolved = resolvePeriodForMeasurement(date, periodList);
      if (resolved && typeof resolved === 'object' && !Array.isArray(resolved)) {
        return resolved;
      }
      // Fallback: build map from groupedMeasurements when resolver returns a period name
      const byPeriod = Object.fromEntries(periodList.map((p) => [p, []]));
      ((groupedMeasurements && groupedMeasurements[date]) || []).forEach((m) => {
        const periodName = typeof resolved === 'string'
          ? resolved
          : (typeof resolvePeriodForMeasurement === 'function' ? resolvePeriodForMeasurement(m) : null);
        const matched = periodList.find(
          (p) => normalizePeriodName(p) === normalizePeriodName(periodName || '')
        );
        if (matched) byPeriod[matched].push(m);
      });
      return byPeriod;
    };

    bucket.dates.forEach((date) => {
      const byPeriod = resolveByPeriod(date);
      const row = [formatDate(date)];
      periodList.forEach((periodName) => {
        const readings = sortMeasurementsByTime(byPeriod[periodName] || []);
        const getWeightInLb = (m) => (m?.weight_lbs != null ? parseFloat(m.weight_lbs) : (m?.weight ? parseFloat(m.weight) : null));
        row.push(
          coloredValueCell(
            readings.map((m) => {
              const value = fixed1(getWeightInLb(m));
              const status = typeof getWeightStatus === 'function'
                ? getWeightStatus(m, periodName)
                : checkWeightValue(getWeightInLb(m), DEFAULT_WEIGHT_TARGET);
              return { text: value, color: getColorForStatus(status) };
            })
          ),
          joinCell(readings.map((m) => fixed1(m.fat || m.body_fat))),
          joinCell(readings.map((m) => fixed1(m.bmi))),
          joinCell(readings.map((m) => (getMeasureTimeRaw(m) ? formatTime(getMeasureTimeRaw(m)) : '')))
        );
      });
      body.push(row);
    });

    const getWeightInLb = (m) => (m?.weight_lbs != null ? parseFloat(m.weight_lbs) : (m?.weight ? parseFloat(m.weight) : null));
    const avgRow = ['Average'];
    periodList.forEach((periodName) => {
      const readings = [];
      bucket.dates.forEach((date) => {
        const byPeriod = resolveByPeriod(date);
        readings.push(...(byPeriod[periodName] || []));
      });
      const wVals = readings.map((m) => getWeightInLb(m)).filter((v) => v != null && !Number.isNaN(v));
      const fVals = readings.map((m) => parseFloat(m.fat || m.body_fat)).filter((v) => !Number.isNaN(v));
      const bVals = readings.map((m) => parseFloat(m.bmi)).filter((v) => !Number.isNaN(v));
      avgRow.push(
        wVals.length ? formatAvg(wVals) : '-',
        fVals.length ? formatAvg(fVals) : '-',
        bVals.length ? formatAvg(bVals) : '-',
        ''
      );
    });
    body.push(avgRow);

    return {
      title: 'Weight',
      subtitle: bucket.label,
      head: [headRow1, headRow2],
      body
    };
  });
}

function buildWeightSummaryTable({
  periodList,
  calculatePeriodStats,
  effectiveMeasurements,
  calculateOverallTargetStats,
  formatPercentCount
}) {
  const headRow1 = [{ content: 'Statistics', rowSpan: 2 }];
  const headRow2 = [];
  const pct = typeof formatPercentCount === 'function'
    ? formatPercentCount
    : (n, t) => (t ? `${Math.round((Number(n || 0) / t) * 100)}% (${n || 0})` : `0% (${n || 0})`);
  const periods = Array.isArray(periodList) && periodList.length
    ? periodList
    : ['Morning', 'Afternoon', 'Evening'];

  periods.forEach((periodName) => {
    headRow1.push({ content: periodName, colSpan: 3 });
    headRow2.push('Weight', 'Body Fat', 'BMI');
  });

  const statTypes = ['Average', 'Maximum', 'Minimum', 'Count', 'Above Target', 'Below Target', 'Within Target'];
  const body = statTypes.map((statType) => {
    const row = [statType];
    periods.forEach((periodName) => {
      const stats = typeof calculatePeriodStats === 'function'
        ? calculatePeriodStats(effectiveMeasurements, periodName)
        : { avg: {}, max: {}, min: {}, count: 0 };
      if (statType === 'Average') {
        row.push(
          stats.avg?.weight ? fixed1(stats.avg.weight) : '',
          stats.avg?.fat ? fixed1(stats.avg.fat) : '',
          stats.avg?.bmi ? fixed1(stats.avg.bmi) : ''
        );
      } else if (statType === 'Maximum') {
        row.push(
          stats.max?.weight ? fixed1(stats.max.weight) : '',
          stats.max?.fat ? fixed1(stats.max.fat) : '',
          stats.max?.bmi ? fixed1(stats.max.bmi) : ''
        );
      } else if (statType === 'Minimum') {
        row.push(
          stats.min?.weight ? fixed1(stats.min.weight) : '',
          stats.min?.fat ? fixed1(stats.min.fat) : '',
          stats.min?.bmi ? fixed1(stats.min.bmi) : ''
        );
      } else if (statType === 'Count') {
        row.push({ content: String(stats.count ?? ''), colSpan: 3 });
      } else if (statType === 'Above Target') {
        row.push({ content: String(stats.above ?? ''), colSpan: 3 });
      } else if (statType === 'Below Target') {
        row.push({ content: String(stats.below ?? ''), colSpan: 3 });
      } else if (statType === 'Within Target') {
        row.push({ content: String(stats.within ?? ''), colSpan: 3 });
      }
    });
    return row;
  });

  const stats = typeof calculateOverallTargetStats === 'function'
    ? calculateOverallTargetStats()
    : {
      above: { weight: 0, bmi: 0, fat: 0 },
      below: { weight: 0, bmi: 0, fat: 0 },
      within: { weight: 0, bmi: 0, fat: 0 }
    };
  const weightTotal = (stats.above?.weight || 0) + (stats.below?.weight || 0) + (stats.within?.weight || 0);
  const bmiTotal = (stats.above?.bmi || 0) + (stats.below?.bmi || 0) + (stats.within?.bmi || 0);
  const fatTotal = (stats.above?.fat || 0) + (stats.below?.fat || 0) + (stats.within?.fat || 0);
  const spanEach = Math.max(periods.length, 1);

  body.push([
    'Total',
    {
      content: `Below target\nWeight: ${pct(stats.below?.weight, weightTotal)}\nBmi: ${pct(stats.below?.bmi, bmiTotal)}\nFat: ${pct(stats.below?.fat, fatTotal)}`,
      colSpan: spanEach
    },
    {
      content: `Within target\nWeight: ${pct(stats.within?.weight, weightTotal)}\nBmi: ${pct(stats.within?.bmi, bmiTotal)}\nFat: ${pct(stats.within?.fat, fatTotal)}`,
      colSpan: spanEach
    },
    {
      content: `Above target\nWeight: ${pct(stats.above?.weight, weightTotal)}\nBmi: ${pct(stats.above?.bmi, bmiTotal)}\nFat: ${pct(stats.above?.fat, fatTotal)}`,
      colSpan: spanEach
    }
  ]);

  return { title: 'Summary Of Measurement', head: [headRow1, headRow2], body };
}

export function exportWeightTablesPdf(config) {
  const periodList =
    config.periodList?.length > 0 ? config.periodList : ['Morning', 'Afternoon', 'Evening'];

  const tables = [
    ...buildWeightMeasurementTables({ ...config, periodList }),
    buildWeightSummaryTable({ ...config, periodList })
  ];

  exportMeasurementTablesPdf({
    filename: config.filename,
    title: config.title || 'Weight Report',
    patient: config.patient,
    tables
  });
}
